<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("calendar") }}</h4>
        <div class="d-flex flex-row">
          <b-button variant="outline-secondary" @click="fetchData">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button class="ml-4" variant="outline-primary" to="/calendar/new" v-if="isCustomer()">
            <b-icon icon="plus"></b-icon> {{ $t("createEvent") }}
          </b-button>
        </div>
      </div>

      <b-row class="mb-3">
        <b-col cols="4">
          <b-button variant="outline-secondary" @click="resetToToday">{{ $t("thisWeek") }}</b-button>
        </b-col>
        <b-col cols="4" class="text-center">
          <b-button variant="outline-secondary" @click="moveWeek(-1)">
            <b-icon-chevron-left/>
          </b-button>
          <div class="d-inline-block mx-3">{{ displayDuration }}</div>
          <b-button variant="outline-secondary" @click="moveWeek(1)">
            <b-icon-chevron-right/>
          </b-button>
        </b-col>
      </b-row>

      <div v-if="isLoading" class="py-3 text-center"><b-spinner/></div>

      <table v-else class="table table-bordered">
        <thead>
        <tr class="text-nowrap">
          <th>{{ $t("date") }}</th>
          <th>{{ $t("datetime") }}</th>
          <th>{{ $t("content") }}</th>
          <th>{{ $t("location") }}</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <template v-for="dayItem in calendars">
          <tr :key="dayItem.date" v-if="dayItem.events.length === 0" :class="{ holiday: dayItem.isHoliday }">
            <td style="width: 140px; white-space: pre;">
              <b>{{ dayItem.displayDate }}</b>
            </td>
            <td colspan="4">
              <span class="text-secondary">{{ $t("noEvent") }}</span>
            </td>
          </tr>
          <tr v-for="(calendarItem, index) in dayItem.events" :key="calendarItem.id" :class="{ holiday: dayItem.isHoliday }">
            <td v-if="index === 0" :rowspan="dayItem.events.length" style="width: 140px; white-space: pre;">
              <b>{{ dayItem.displayDate }}</b>
            </td>
            <td v-if="calendarItem.rowSpan" :rowspan="calendarItem.rowSpan" >
              <div :class="calendarItem.textClassObj" style="white-space: pre">{{ calendarItem.displayTime }}</div>
              <div class="text-secondary">{{ calendarItem.displayStatusText }}</div>
            </td>
            <td class="text-break" :class="calendarItem.textClassObj">
              <div class="mb-1"><b>{{ calendarItem.subject }}</b></div>
              <div v-if="calendarItem.chairman" class="mb-1">
                <span class="text-secondary">{{ $t("chairman") }}:</span> {{ calendarItem.chairman }}
              </div>
              <div v-if="calendarItem.meetingPreparation" class="mb-1">
                <span class="text-secondary">{{ $t("eventPreparation") }}:</span> {{ calendarItem.meetingPreparation }}
              </div>
              <div v-if="calendarItem.participants" class="mb-1">
                <span class="text-secondary">{{ $t("eventParticipants") }}:</span> {{ calendarItem.participants }}
              </div>
              <div v-if="calendarItem.invitees" class="mb-1">
                <span class="text-secondary">{{ $t("eventInvitees") }}:</span> {{ calendarItem.invitees }}
              </div>
              <div v-if="calendarItem.description" class="mb-1">
                <span class="text-secondary">{{ $t("note") }}:</span> {{ calendarItem.description }}
              </div>
            </td>
            <td class="text-break" :class="calendarItem.textClassObj" style="min-width: 160px;">{{ calendarItem.location }}</td>
            <td>
              <div v-if="calendarItem.canEdit">
                <b-button variant="link" @click="onClickEdit(calendarItem)">{{ $t("edit") }}</b-button>
              </div>
              <div>
                <b-button variant="link" class="text-danger" @click="onClickDelete(calendarItem)">{{ $t("actionDelete") }}</b-button>
              </div>
            </td>
          </tr>
        </template>
        </tbody>
      </table>

    </b-container>
  </main>
</template>

<style lang="scss" scoped>
  tr.holiday {
    background-color: rgba(255, 0, 0, 0.1);
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import {DateTime, Duration} from "luxon";
  import BaseComponent from "@/components/BaseComponent";

  import * as calendarService from "@/service/calendarService";


  @Component
  export default class CalendarList extends BaseComponent {

    startDate: DateTime = null;
    endDate: DateTime = null;
    isLoading = false;

    calendars = [];

    mounted() {
      this.resetToToday();
    }

    get displayDuration() {
      if (this.startDate && this.endDate) {
        const displayStartDate = this.startDate.toFormat("dd/MM/yyyy");
        const displayEndDate = this.endDate.toFormat("dd/MM/yyyy");

        return displayStartDate + " - " + displayEndDate;
      }
    }

    resetToToday() {
      const now = DateTime.now();
      if (+now >= this.startDate?.valueOf() && +now <= this.endDate?.valueOf()) {
        // scroll

      } else {
        this.startDate = now.startOf("week");
        this.endDate = now.endOf("week");
        this.fetchData();
      }
    }

    moveWeek(increment: number) {
      const duration = Duration.fromObject({ weeks: increment });
      this.startDate = this.startDate.plus(duration);
      this.endDate = this.endDate.plus(duration);
      this.fetchData();
    }

    async fetchData() {
      const startDate = this.startDate;

      try {
        this.isLoading = true;
        const calendars = await calendarService.getCalendars(
          this.startDate.toFormat("yyyy-MM-dd"),
          this.endDate.toFormat("yyyy-MM-dd")
        );

        if (+this.startDate === +startDate) {
          for (const dayItem of calendars) {
            const date = DateTime.fromFormat(dayItem.date, "dd/MM/yyyy");
            dayItem.displayDate = date.toFormat("cccc\ndd/MM/yyyy", { locale: "vi-VN" });
            dayItem.isHoliday = [6, 7].includes(date.weekday); // saturday, sunday

            for (const calendarItem of dayItem.events) {
              const startAt = DateTime.fromISO(calendarItem.startAt);
              const endAt = calendarItem.endAt? DateTime.fromISO(calendarItem.endAt) : undefined;

              if (endAt) {
                calendarItem.canEdit = endAt.endOf("day").diffNow().toMillis() > 0;
                if (endAt.hasSame(startAt, "day")) {
                  calendarItem.displayTime = startAt.toFormat("HH:mm") + " - " + endAt.toFormat("HH:mm");
                } else {
                  calendarItem.displayTime = startAt.toFormat("HH:mm") + " -\n" + endAt.toFormat("HH:mm (dd/MM/yyyy)");
                }
              } else {
                calendarItem.canEdit = startAt.endOf("day").diffNow().toMillis() > 0;
                calendarItem.displayTime = startAt.toFormat("HH:mm");
              }

              calendarItem.displayStatusText = {
                0: this.$t("eventStatusPending"),
                2: this.$t("eventStatusCancelled")
              }[calendarItem.status] || "";

              calendarItem.textClassObj = {
                'text-secondary': calendarItem.status === 0,
                'text-strikethrough': calendarItem.status === 2,
              };
            }

            for (let i = 0; i < dayItem.events.length; i++) {
              const calendarItem = dayItem.events[i];
              calendarItem.rowSpan = 1;
              for (let j = i + 1; j < dayItem.events.length; j++) {
                const nextItem = dayItem.events[j];
                if (
                  calendarItem.startAt === nextItem.startAt &&
                  calendarItem.endAt === nextItem.endAt &&
                  calendarItem.status === nextItem.status
                ) {
                  calendarItem.rowSpan++;
                  i++;
                } else break;
              }
            }
          }
          this.calendars = calendars;
        }

      } catch (e) {
        this.toastError(e);

      } finally {
        if (+this.startDate === +startDate) {
          this.isLoading = false;
        }
      }
    }

    onClickEdit(calendarItem) {
      sessionStorage.setItem("editCalendar", JSON.stringify(calendarItem));
      this.$router.push("/calendar/edit");
    }

    async onClickDelete(calendarItem) {
      const modalMessage = this.$t("deleteEventConfirm", { subject: calendarItem.subject }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await calendarService.deleteCalendar(calendarItem.id);
          this.toastSuccessDelay(this.$t("deleteEventSuccessful").toString());
          this.fetchData();

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }
</script>
